/**
 * Watches aura instances whose AuraDef has `explodeOnExpire` or `jumpOnDispel` and reacts once
 * they are gone, without hooks in combat:
 * - gone at/after its expiry time → it expired naturally → explode around the holder;
 * - gone early while the holder lives → it was dispelled (or otherwise stripped) → jump to the
 *   nearest ally of the holder. Consumed stacks (Bone Shards) are not explosions: an
 *   explodeOnExpire aura removed early simply vanishes.
 * Removal happens between two mechanics updates, so the decision uses the last seen state.
 */
import type { AuraDef, EffectCtx, SimContext, Unit, WatchedAuraState } from '../../contracts';
import { dist, gatePasses, mstate, pushZone, runEffects, scaleAmount, unitOf } from './state';

/** Derived cache: aura ids that need watching (rebuilt per registry). */
const watchCache = new WeakMap<object, Set<string>>();

function watchedIds(ctx: SimContext): Set<string> {
  let s = watchCache.get(ctx.content);
  if (!s) {
    s = new Set();
    for (const [id, def] of ctx.content.auras) if (def.explodeOnExpire || def.jumpOnDispel) s.add(id);
    watchCache.set(ctx.content, s);
  }
  return s;
}

function explode(ctx: SimContext, def: AuraDef, w: WatchedAuraState, holder: Unit): void {
  const spec = def.explodeOnExpire!;
  const caster = unitOf(ctx, w.casterId);
  if (!caster) return;
  const center = { x: holder.pos.x, y: holder.pos.y };
  const hits = ctx.sys.spatial.unitsInRadius(ctx.world, center, spec.radius, { affects: 'allies', relativeTo: holder.id });
  const ids = spec.hitsHolder === false ? hits.filter((id) => id !== holder.id) : hits.includes(holder.id) || !holder.alive ? hits : [holder.id, ...hits];
  const dmg = spec.damage
    ? [{ type: 'damage' as const, school: spec.damage.school, amount: spec.damage.perStack ? scaleAmount(spec.damage.amount, Math.max(1, w.stacks)) : spec.damage.amount, avoidable: false, isAoE: true }]
    : [];
  for (const id of ids) {
    const u = ctx.world.units[id];
    if (!u?.alive) continue;
    const ectx: EffectCtx = { sourceId: caster.id, abilityId: w.auraId, targetId: id, point: center, depth: 1 };
    runEffects(ctx, dmg, ectx);
    runEffects(ctx, spec.effects, ectx);
  }
  if (spec.pool && (spec.poolMinKey === undefined || gatePasses(ctx, { minKey: spec.poolMinKey }))) {
    const area = ctx.sys.spatial.resolveArea(ctx.world, spec.pool.shape, 'point', caster.id, null, center);
    pushZone(ctx, caster.id, w.auraId, spec.pool, area, 'avoid');
  }
}

function jump(ctx: SimContext, def: AuraDef, w: WatchedAuraState, holder: Unit): void {
  const spec = def.jumpOnDispel!;
  const caster = unitOf(ctx, w.casterId);
  if (!caster) return;
  let best: Unit | undefined;
  let bestD = Infinity;
  for (const id of ctx.world.unitOrder) {
    const u = ctx.world.units[id];
    if (!u?.alive || u.id === holder.id || u.faction !== holder.faction) continue;
    const d = dist(u.pos, holder.pos);
    if (d <= spec.radius && d < bestD) {
      bestD = d;
      best = u;
    }
  }
  if (!best) return;
  const remaining = w.expiresAt === null ? def.durationMs : Math.max(1, w.expiresAt - ctx.now);
  const durationMs = spec.refresh ? def.durationMs : remaining;
  ctx.sys.combat.applyAura(ctx, best.id, def.id, caster.id, w.abilityId, { durationMs, stacks: Math.max(1, w.stacks) });
  runEffects(ctx, spec.effects, { sourceId: caster.id, abilityId: def.id, targetId: best.id, point: { x: best.pos.x, y: best.pos.y }, depth: 1 });
}

export function updateAuraWatch(ctx: SimContext): void {
  const ids = watchedIds(ctx);
  if (ids.size === 0) return;
  const world = ctx.world;
  const st = mstate(world);
  const live = new Map<string, true>();
  // 1. find / refresh live instances.
  for (const uid of world.unitOrder) {
    const u = world.units[uid];
    if (!u?.alive || u.auras.length === 0) continue;
    for (const a of u.auras) {
      if (!ids.has(a.auraId)) continue;
      live.set(a.iid, true);
      const w = st.watched.find((x) => x.iid === a.iid);
      if (w) {
        w.stacks = a.stacks;
        w.expiresAt = a.expiresAt;
        w.casterId = a.casterId;
      } else st.watched.push({ iid: a.iid, unitId: u.id, auraId: a.auraId, casterId: a.casterId, abilityId: a.abilityId, stacks: a.stacks, expiresAt: a.expiresAt });
    }
  }
  if (st.watched.length === live.size) return;
  // 2. react to the ones that vanished.
  const gone = st.watched.filter((w) => !live.has(w.iid));
  st.watched = st.watched.filter((w) => live.has(w.iid));
  for (const w of gone) {
    const def = ctx.content.aura(w.auraId);
    const holder = world.units[w.unitId];
    if (!def || !holder) continue;
    const expired = w.expiresAt !== null && w.expiresAt <= ctx.now;
    if (expired && def.explodeOnExpire && holder.alive) explode(ctx, def, w, holder);
    else if (!expired && def.jumpOnDispel && holder.alive) jump(ctx, def, w, holder);
  }
}
